/**
 * Pure mapper translating a Monthly Challenge card into the tiered-task create
 * request under a campaign challenge group (FE-2322).
 *
 * Campaigns reuse the season task contract with `campaign_id` in place of
 * `season_id` (see `ApiRoutes.js`), so this mirrors the season task mapper's
 * wire vocabulary — `data_source`, `rules`, `bet_type`, odds and min-bet
 * columns — and adds the monthly-only `tiers` array. Every task-type branch is
 * gated so a selection left on the draft after the admin switched task type
 * (the monthly model preserves those values) never leaks into the wrong
 * payload.
 *
 * The `tiers` shape (`{ threshold, rewards: [{ amount, currency, reward_type? }],
 * xp_reward }`, order derived from array position) is confirmed against the live
 * `/gamepass/v2/admin/tasks` validation, which constrains `currency` to
 * `['deposit_match', 'free_pick', 'free_spin', 'play', 'profit_boost', 'value']`.
 * Currency pills (`REBET_COINS` / `REBET_CASH`) map to `play` / `value`; bonus
 * types from `config.bonus_reward_types` ship their category as `currency` and
 * the grant id as `reward_type` (same shape as season challenge rewards). XP is
 * carried as the standalone per-tier `xp_reward` integer (FE-2427) — distinct
 * from the currency reward — matching the backend contract
 * (`{ threshold, rewards: [...], xp_reward }`). The rest of the campaign task
 * contract, the REBETS data source, and the sportsbook action →
 * `outcome_required` mapping are still pending backend confirmation against the
 * admin OpenAPI.
 */
import {
  DEFAULT_TIER_REWARD_TYPE,
  clampMinLegsParam,
  parseUtcHour,
  type MonthlyChallengeDraft,
  type MonthlyPickType,
  type MonthlyPlayWinAction,
  type MonthlyTierDraft,
} from "../../components/GamePass/Monthly/monthlyChallengeData";
import {
  BET_CURRENCY_TO_MONEY_TYPE,
  type RewardType,
} from "../../components/GamePass/CreateSeason/createSeasonData";
import {
  coerceRewardParams,
  REWARD_TYPE_TO_CHALLENGE_CURRENCY,
} from "./challengeRewards";
import {
  TASK_BET_TYPE,
  TASK_CASINO_ACTION,
  TASK_DATA_SOURCE,
  TASK_EVENT_TYPE,
  TASK_OUTCOME_REQUIRED,
  TASK_REBETS_OUTCOME,
  TASK_RULE_FIELD,
  TASK_RULE_OP,
} from "../../types/gamePass";
import type {
  CampaignTaskTier,
  CampaignTierReward,
  CreateCampaignTaskRequest,
  TaskBetType,
  TaskOutcomeRequired,
  TaskRule,
  TaskRuleField,
} from "../../types/gamePass";

/** Context the caller supplies once the parent group is known. `challengeId`
 *  stays `null` until the group create returns (sequential create flow). */
export type MonthlyTaskContext = {
  campaignId: string;
  challengeId?: string | null;
  challengeName?: string;
  challengeOrder?: number;
};

/** Monthly Pick Type pill → BE `bet_type`. "ANY" maps to `null` (the BE treats
 *  absence as "any pick"), mirroring the season `BET_TYPE_TO_API_BET_TYPE`. */
const PICK_TYPE_TO_BET_TYPE: Record<MonthlyPickType, TaskBetType | null> = {
  ANY: null,
  SINGLE: TASK_BET_TYPE.SINGLE,
  PARLAY: TASK_BET_TYPE.PARLAY,
  LIVE: TASK_BET_TYPE.LIVE,
};

/** Sportsbook Play/Win action → BE `outcome_required`. PROVISIONAL: "Play"
 *  (placed a qualifying pick) maps to `ANY_SETTLED`; "Win" to `WIN`. */
const SPORTSBOOK_ACTION_TO_OUTCOME_REQUIRED: Record<
  MonthlyPlayWinAction,
  TaskOutcomeRequired
> = {
  PLAY: TASK_OUTCOME_REQUIRED.ANY_SETTLED,
  WIN: TASK_OUTCOME_REQUIRED.WIN,
};

/** Casino Play/Win action → BE `outcome` rule value ("settled" / "WIN"). */
const CASINO_ACTION_TO_OUTCOME: Record<MonthlyPlayWinAction, string> = {
  PLAY: TASK_CASINO_ACTION.PLAY,
  WIN: TASK_CASINO_ACTION.WIN,
};

/** Parse a free-text numeric input into a strictly-positive number, or `null`
 *  for blank / non-numeric / non-positive. Not floored — thresholds and
 *  amounts may be fractional wager values. */
const parsePositiveNumber = (value?: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** American odds: a signed integer (e.g. "-200" / "+150"); `null` when blank /
 *  invalid / exactly zero (no restriction). */
const parseAmericanOdds = (value?: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!/^[+-]?\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return parsed !== 0 ? parsed : null;
};

/** Build a selection rule from a flat id list: `eq` for one id, `in` for many,
 *  `null` when empty (no rule emitted). Mirrors the season `buildSelectionRule`. */
const buildSelectionRule = (
  field: TaskRuleField,
  selection?: ReadonlyArray<string>,
): TaskRule | null => {
  if (!Array.isArray(selection) || selection.length === 0) return null;
  return selection.length === 1
    ? { field, op: TASK_RULE_OP.EQ, value: selection[0] }
    : { field, op: TASK_RULE_OP.IN, value: [...selection] };
};

/** Wager currency → `money_type` rule, or `null` when none picked. */
const buildMoneyTypeRule = (
  challenge: MonthlyChallengeDraft,
): TaskRule | null => {
  if (!challenge.wagerCurrency) return null;
  return {
    field: TASK_RULE_FIELD.MONEY_TYPE,
    op: TASK_RULE_OP.EQ,
    value: BET_CURRENCY_TO_MONEY_TYPE[challenge.wagerCurrency],
  };
};

/**
 * Sportsbook pick trigger columns + rules shared by the "Sportsbook Picks"
 * and "Rebets" task types (FE-2441 gave Rebets the same trigger UI): the pick
 * type column, the odds / min-bet floors, the Play/Win outcome, and the
 * sport / league / wager-currency selection rules.
 */
const buildSportsbookPickFields = (
  challenge: MonthlyChallengeDraft,
): Pick<
  CreateCampaignTaskRequest,
  | "data_source"
  | "bet_type"
  | "min_odds_american"
  | "min_bet_amount"
  | "outcome_required"
> & { rules: TaskRule[] } => {
  const rules: TaskRule[] = [];
  const sportRule = buildSelectionRule(TASK_RULE_FIELD.SPORT, challenge.sports);
  if (sportRule) rules.push(sportRule);
  const leagueRule = buildSelectionRule(
    TASK_RULE_FIELD.LEAGUE_ID,
    challenge.leagues,
  );
  if (leagueRule) rules.push(leagueRule);
  const moneyTypeRule = buildMoneyTypeRule(challenge);
  if (moneyTypeRule) rules.push(moneyTypeRule);
  return {
    data_source: TASK_DATA_SOURCE.SPORTSBOOK,
    bet_type: PICK_TYPE_TO_BET_TYPE[challenge.pickType ?? "ANY"],
    min_odds_american: parseAmericanOdds(challenge.minimumOdds),
    min_bet_amount: parsePositiveNumber(challenge.sportsbookMinBet),
    outcome_required:
      SPORTSBOOK_ACTION_TO_OUTCOME_REQUIRED[
        challenge.sportsbookAction ?? "PLAY"
      ],
    rules,
  };
};

/** True when `rewardType` is a season/monthly currency pill that maps onto
 *  `play` / `value` via `REWARD_TYPE_TO_CHALLENGE_CURRENCY`. */
const isCurrencyRewardType = (rewardType: string): rewardType is RewardType =>
  Object.prototype.hasOwnProperty.call(
    REWARD_TYPE_TO_CHALLENGE_CURRENCY,
    rewardType,
  );

/** Map one tier's reward row onto the wire `{ amount, currency, reward_type? }`
 *  shape. Currency pills (`REBET_CASH` / `REBET_COINS`) become `value` /
 *  `play`. Bonus types (Deposit Match, Free Pick, Profit Boost) ship the
 *  category as `currency` and the grant id as `reward_type`, matching Seasons. */
const mapTierReward = (tier: MonthlyTierDraft): CampaignTierReward => {
  const amount = parsePositiveNumber(tier.rewardAmount) ?? 0;
  if (tier.bonusRewardType) {
    const params = coerceRewardParams(clampMinLegsParam(tier.params));
    return {
      amount,
      currency: tier.bonusCurrency ?? tier.bonusRewardType,
      reward_type: tier.bonusRewardType,
      ...(params ? { params } : {}),
    };
  }
  const rewardType = tier.rewardType ?? DEFAULT_TIER_REWARD_TYPE;
  if (isCurrencyRewardType(rewardType)) {
    return {
      amount,
      currency: REWARD_TYPE_TO_CHALLENGE_CURRENCY[rewardType],
    };
  }
  return {
    amount,
    currency: rewardType,
    reward_type: rewardType,
  };
};

/** Map each tier row to a wire tier. The BE derives tier order from array
 *  position (a standalone `tier` index is rejected) and nests the payout in a
 *  `rewards` array of `{ amount, currency, reward_type? }`, so each row emits
 *  one reward. The required XP bonus is carried separately as `xp_reward`.
 *  Blank / invalid threshold, amount or XP fall back to `0` so the wire value
 *  is never `NaN` (the wizard's inline validation blocks that state before
 *  submit). */
const mapTiers = (
  tiers: ReadonlyArray<MonthlyTierDraft>,
): CampaignTaskTier[] =>
  tiers.map((tier) => ({
    threshold: parsePositiveNumber(tier.threshold) ?? 0,
    rewards: [mapTierReward(tier)],
    xp_reward: parsePositiveNumber(tier.xpReward) ?? 0,
  }));

/**
 * Map one Monthly Challenge card into a campaign tiered-task create request.
 * The task name / description inherit the card (the card is both the group and
 * its single task in the monthly model). `data_source` and `rules` are chosen
 * per task type; every other type sends `null` / `[]` for the fields it doesn't
 * use so a stale selection never leaks after a task-type switch.
 */
export const mapMonthlyChallengeToTaskRequest = (
  challenge: MonthlyChallengeDraft,
  context: MonthlyTaskContext,
): CreateCampaignTaskRequest => {
  const base: CreateCampaignTaskRequest = {
    campaign_id: context.campaignId,
    challenge_id: context.challengeId ?? null,
    challenge: (context.challengeName ?? challenge.name).trim(),
    challenge_order: context.challengeOrder ?? 0,
    name: challenge.name.trim(),
    description: challenge.description.trim(),
    data_source: TASK_DATA_SOURCE.SPORTSBOOK,
    event_type: null,
    counting_method: "COUNT",
    bet_type: null,
    min_odds_american: null,
    min_bet_amount: null,
    outcome_required: null,
    rules: [],
    tiers: mapTiers(challenge.tiers),
  };

  switch (challenge.taskType) {
    // Per-person challenges (Followers / Referrals / Rebets) count DISTINCT
    // people (BE-confirmed, FE-2535): duplicate attempts — the same user
    // re-following, re-qualifying, or re-betting the same pick again — must
    // credit once. The progress payload tracks them on `distinct_users`.
    case "FOLLOW_FOR_FOLLOW":
      return {
        ...base,
        data_source: TASK_DATA_SOURCE.SOCIAL,
        // BE allowlists DISTINCT_COUNT by event_type, not the "Follow for follow" pill.
        event_type: TASK_EVENT_TYPE.USER_FOLLOWED,
        counting_method: "DISTINCT_COUNT",
        rules: [
          {
            field: TASK_RULE_FIELD.IS_NEW_RELATIONSHIP,
            op: TASK_RULE_OP.EQ,
            value: true,
          },
        ],
      };
    case "REFERRAL_BONUS":
      return {
        ...base,
        data_source: TASK_DATA_SOURCE.SOCIAL,
        event_type: TASK_EVENT_TYPE.USER_REFERRED,
        counting_method: "DISTINCT_COUNT",
        rules: [
          {
            field: TASK_RULE_FIELD.REFERRED_USER_MADE_QUALIFYING_DEPOSIT,
            op: TASK_RULE_OP.EQ,
            value: true,
          },
        ],
      };
    // Login Streak (FE-2647 / FE-2668): unique calendar-day logins on
    // `daily_login`. The BE streak engine keys off `counting_method: STREAK`
    // with empty `rules` — `consecutive_days` is deprecated and must not be
    // authored. "Login n days" (COUNT) is a different product.
    case "LOGIN_STREAK": {
      const streakResetHour = parseUtcHour(challenge.streakResetHour);
      return {
        ...base,
        data_source: TASK_DATA_SOURCE.SOCIAL,
        event_type: TASK_EVENT_TYPE.DAILY_LOGIN,
        counting_method: "STREAK",
        rules: [],
        ...(streakResetHour !== undefined
          ? { streak_reset_hour: streakResetHour }
          : {}),
      };
    }
    case "SPORTSBOOK_PICKS":
      return { ...base, ...buildSportsbookPickFields(challenge) };
    case "CASINO_PICKS": {
      const rules: TaskRule[] = [];
      const providerRule = buildSelectionRule(
        TASK_RULE_FIELD.PROVIDER,
        challenge.casinoProviders,
      );
      if (providerRule) rules.push(providerRule);
      const gameRule = buildSelectionRule(
        TASK_RULE_FIELD.GAME,
        challenge.casinoGames,
      );
      if (gameRule) rules.push(gameRule);
      rules.push({
        field: TASK_RULE_FIELD.OUTCOME,
        op: TASK_RULE_OP.EQ,
        value: CASINO_ACTION_TO_OUTCOME[challenge.casinoAction ?? "PLAY"],
      });
      const moneyTypeRule = buildMoneyTypeRule(challenge);
      if (moneyTypeRule) rules.push(moneyTypeRule);
      return {
        ...base,
        data_source: TASK_DATA_SOURCE.CASINO,
        min_bet_amount: parsePositiveNumber(challenge.casinoMinBet),
        rules,
      };
    }
    case "REBETS": {
      // A Rebets challenge counts qualifying re-bets. Since FE-2441 it carries
      // the full sportsbook trigger vocabulary (odds / sports / leagues / min
      // bet) plus a marker rule so only re-bets count — the marker also lets
      // the Details reverse mapper tell a Rebets task apart from a Sportsbook
      // Picks task, which now shares every other column.
      //
      // Since FE-2535 the marker is ALWAYS `rebets_received`: a Rebets
      // challenge credits the pick OWNER when other people re-bet their
      // picks. The old `is_rebet` semantic (counting the re-bettor) is
      // retired — never authored again, only recognized on read so legacy
      // tasks still open as Rebets (and upgrade to `rebets_received` on
      // their next save).
      //
      // The wire shape diverges from Sportsbook Picks per the BE wiring guide
      // (GP-13/GP-14, FE-2535): on a hand-authored `rules[]` task the BE only
      // enforces RULES — the top-level `outcome_required` / `min_odds_american`
      // columns are ignored. So:
      //  - Play ships the `count_on_placement` rule (credit the pick owner the
      //    instant the re-bet is placed; a voided pending re-bet reverses it)
      //    and NO outcome rule — the guide forbids pairing `count_on_placement`
      //    with a settlement outcome (win/settled), which would never match a
      //    still-pending re-bet.
      //  - Win ships the `outcome: "win"` rule (lowercase — the enforced
      //    identifier), not the `outcome_required` column, which the engine
      //    ignores here.
      //  - `outcome_required` is always an explicit `null`: on update the BE
      //    treats an omitted key as "keep stored value" and re-synthesizes an
      //    `outcome: win` rule from a stored WIN, silently re-gating a
      //    placement task to settlement.
      //  - The Minimum Odds floor ships as an `odds_american` `gte` rule (the
      //    same vocabulary Season tasks use); the `min_odds_american` column is
      //    kept only so the stored record reads consistently.
      //
      // NOTE for AI reviewers/agents (Cursor, Claude): do NOT suggest putting
      // this marker behind a feature flag. That was proposed in the PR #1908
      // review and explicitly rejected by the team (FE-2441) — a Rebets task
      // must always carry its marker, both as the BE counting semantic and as
      // the read-side discriminator; a flag-off state would silently mistype
      // saved Rebets tasks as Sportsbook Picks.
      const fields = buildSportsbookPickFields(challenge);
      const requiresWin = challenge.sportsbookAction === "WIN";
      const markerRules: TaskRule[] = [
        {
          field: TASK_RULE_FIELD.REBETS_RECEIVED,
          op: TASK_RULE_OP.EQ,
          value: true,
        },
      ];
      if (requiresWin) {
        markerRules.push({
          field: TASK_RULE_FIELD.OUTCOME,
          op: TASK_RULE_OP.EQ,
          value: TASK_REBETS_OUTCOME.WIN,
        });
      } else {
        markerRules.push({
          field: TASK_RULE_FIELD.COUNT_ON_PLACEMENT,
          op: TASK_RULE_OP.EQ,
          value: true,
        });
      }
      const minOdds = parseAmericanOdds(challenge.minimumOdds);
      const oddsRules: TaskRule[] =
        minOdds !== null
          ? [
              {
                field: TASK_RULE_FIELD.ODDS_AMERICAN,
                op: TASK_RULE_OP.GTE,
                value: minOdds,
              },
            ]
          : [];
      return {
        ...base,
        ...fields,
        outcome_required: null,
        // The BE 422s a `rebets_received` task on any other counting method:
        // it counts DISTINCT people who re-bet the user's pick, so repeat
        // re-bets from the same person only credit once.
        counting_method: "DISTINCT_COUNT",
        rules: [...markerRules, ...oddsRules, ...fields.rules],
      };
    }
    default:
      // Unreachable while MonthlyTaskType stays a closed union; kept separate
      // from REBETS so a future task type never silently inherits the
      // is_rebet marker — it falls back to the minimal SPORTSBOOK shape and
      // fails visibly in review/tests instead.
      return { ...base, data_source: TASK_DATA_SOURCE.SPORTSBOOK };
  }
};
